import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Heart, 
  Activity, 
  Moon, 
  Droplets, 
  Apple, 
  Smartphone,
  Shield,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw as Sync,
  Settings,
  BarChart3,
  Calendar,
  Target,
  Zap,
  Thermometer,
  Gauge
} from 'lucide-react';

interface HealthData {
  steps: number;
  heartRate: {
    resting: number;
    average: number;
    max: number;
    zones: {
      fatBurn: number;
      cardio: number;
      peak: number;
    };
  };
  sleep: {
    duration: number;
    quality: number;
    stages: {
      deep: number;
      light: number;
      rem: number;
      awake: number;
    };
    efficiency: number;
  };
  calories: {
    burned: number;
    consumed: number;
    net: number;
  };
  weight: number;
  bodyFat: number;
  bloodPressure?: {
    systolic: number;
    diastolic: number;
  };
  bloodGlucose?: number;
  oxygenSaturation?: number;
  timestamp: Date;
  source: 'apple_health' | 'google_fit' | 'samsung_health' | 'manual';
}

interface HealthInsight {
  id: string;
  type: 'trend' | 'anomaly' | 'recommendation' | 'achievement' | 'warning';
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  actionable: boolean;
  recommendations?: string[];
  data: any;
  timestamp: Date;
}

interface SyncStatus {
  platform: string;
  lastSync: Date;
  status: 'connected' | 'disconnected' | 'error' | 'syncing';
  dataTypes: string[];
  errorMessage?: string;
  nextSync?: Date;
}

const HealthDataPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [healthData, setHealthData] = useState<HealthData | null>(null);
  const [insights, setInsights] = useState<HealthInsight[]>([]);
  const [syncStatuses, setSyncStatuses] = useState<SyncStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadHealthData();
  }, []);

  const loadHealthData = async () => {
    try {
      setLoading(true);
      const [healthDataResult, insightsResult, syncStatusesResult] = await Promise.all([
        loadCurrentHealthData(),
        loadHealthInsights(),
        loadSyncStatuses()
      ]);

      setHealthData(healthDataResult);
      setInsights(insightsResult);
      setSyncStatuses(syncStatusesResult);
    } catch (error) {
      console.error('Error loading health data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentHealthData = async (): Promise<HealthData> => {
    // Mock data - replace with actual API call
    return {
      steps: 8500,
      heartRate: {
        resting: 65,
        average: 75,
        max: 180,
        zones: {
          fatBurn: 108,
          cardio: 126,
          peak: 153
        }
      },
      sleep: {
        duration: 7.5,
        quality: 85,
        stages: {
          deep: 2,
          light: 4,
          rem: 1.5,
          awake: 0.5
        },
        efficiency: 93.75
      },
      calories: {
        burned: 2200,
        consumed: 1800,
        net: -400
      },
      weight: 150,
      bodyFat: 20,
      bloodPressure: {
        systolic: 120,
        diastolic: 80
      },
      bloodGlucose: 95,
      oxygenSaturation: 98,
      timestamp: new Date(),
      source: 'apple_health'
    };
  };

  const loadHealthInsights = async (): Promise<HealthInsight[]> => {
    // Mock data - replace with actual API call
    return [
      {
        id: 'insight_1',
        type: 'recommendation',
        title: 'Great Sleep Quality',
        description: 'Your sleep quality score of 85% is excellent. Keep up the good work!',
        severity: 'low',
        actionable: false,
        data: { quality: 85 },
        timestamp: new Date()
      },
      {
        id: 'insight_2',
        type: 'recommendation',
        title: 'Increase Daily Steps',
        description: 'You\'ve taken 8,500 steps today. Consider adding a short walk to reach your 10,000 step goal.',
        severity: 'medium',
        actionable: true,
        recommendations: [
          'Take a 10-minute walk',
          'Use stairs instead of elevator',
          'Park farther from your destination'
        ],
        data: { steps: 8500, goal: 10000 },
        timestamp: new Date()
      },
      {
        id: 'insight_3',
        type: 'trend',
        title: 'Heart Rate Trend',
        description: 'Your resting heart rate has improved by 5 BPM over the last month.',
        severity: 'low',
        actionable: false,
        data: { improvement: 5, period: 'month' },
        timestamp: new Date()
      }
    ];
  };

  const loadSyncStatuses = async (): Promise<SyncStatus[]> => {
    // Mock data - replace with actual API call
    return [
      {
        platform: 'apple_health',
        lastSync: new Date(),
        status: 'connected',
        dataTypes: ['steps', 'heartRate', 'sleep', 'calories', 'weight'],
        nextSync: new Date(Date.now() + 24 * 60 * 60 * 1000)
      },
      {
        platform: 'google_fit',
        lastSync: new Date(Date.now() - 2 * 60 * 60 * 1000),
        status: 'connected',
        dataTypes: ['steps', 'heartRate', 'sleep'],
        nextSync: new Date(Date.now() + 22 * 60 * 60 * 1000)
      },
      {
        platform: 'samsung_health',
        lastSync: new Date(Date.now() - 24 * 60 * 60 * 1000),
        status: 'disconnected',
        dataTypes: [],
        errorMessage: 'Connection lost. Please reconnect.'
      }
    ];
  };

  const syncHealthData = async (platform: string) => {
    try {
      setSyncing(true);
      // Implementation would sync health data
      console.log('Syncing health data from:', platform);
      
      // Simulate sync delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Reload data
      await loadHealthData();
      
      alert('Health data synced successfully!');
    } catch (error) {
      console.error('Error syncing health data:', error);
      alert('Error syncing health data. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const connectHealthPlatform = async (platform: string) => {
    try {
      // Implementation would connect to health platform
      console.log('Connecting to health platform:', platform);
      alert(`Connecting to ${platform}...`);
    } catch (error) {
      console.error('Error connecting to health platform:', error);
      alert('Error connecting to health platform. Please try again.');
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'connected': return 'text-green-600 bg-green-100';
      case 'disconnected': return 'text-red-600 bg-red-100';
      case 'error': return 'text-red-600 bg-red-100';
      case 'syncing': return 'text-[var(--color-primary)] bg-[var(--color-primary)]/10';
      default: return 'text-[var(--color-text-muted)]' + ' ' + 'bg-[var(--color-background-secondary)]';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected': return <CheckCircle className="w-4 h-4" />;
      case 'disconnected': return <AlertTriangle className="w-4 h-4" />;
      case 'error': return <AlertTriangle className="w-4 h-4" />;
      case 'syncing': return <Sync className="w-4 h-4 animate-spin" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-[var(--color-text-muted)]' + ' ' + 'bg-[var(--color-background-secondary)]';
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'trend': return <TrendingUp className="w-4 h-4" />;
      case 'anomaly': return <AlertTriangle className="w-4 h-4" />;
      case 'recommendation': return <Target className="w-4 h-4" />;
      case 'achievement': return <CheckCircle className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      default: return <BarChart3 className="w-4 h-4" />;
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'apple_health': return <Apple className="w-4 h-4" />;
      case 'google_fit': return <Smartphone className="w-4 h-4" />;
      case 'samsung_health': return <Smartphone className="w-4 h-4" />;
      default: return <Shield className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-end">
        <div className="text-right">
          <div className="text-3xl font-bold text-blue-600">
            {healthData ? Math.round(healthData.sleep.quality) : 0}%
          </div>
          <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Sleep Quality</div>
        </div>
      </div>

      {/* Health Overview */}
      {healthData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="sculptd-card-elevated sculptd-sheen" style={{
            boxShadow: 'var(--shadow-card-separation)'
          }}>
            <div className="p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-full" style={{ background: 'var(--color-primary)/10' }}>
                  <Activity className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{ 
                    color: 'var(--color-text)',
                    textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}>{healthData.steps.toLocaleString()}</div>
                  <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Steps Today</div>
                </div>
              </div>
            </div>
          </div>

          <div className="sculptd-card-elevated sculptd-sheen" style={{
            boxShadow: 'var(--shadow-card-separation)'
          }}>
            <div className="p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-full">
                  <Heart className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{ 
                    color: 'var(--color-text)',
                    textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}>{healthData.heartRate.resting}</div>
                  <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Resting HR (BPM)</div>
                </div>
              </div>
            </div>
          </div>

          <div className="sculptd-card-elevated sculptd-sheen" style={{
            boxShadow: 'var(--shadow-card-separation)'
          }}>
            <div className="p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-full">
                  <Moon className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{ 
                    color: 'var(--color-text)',
                    textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}>{healthData.sleep.duration}h</div>
                  <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Sleep Duration</div>
                </div>
              </div>
            </div>
          </div>

          <div className="sculptd-card-elevated sculptd-sheen" style={{
            boxShadow: 'var(--shadow-card-separation)'
          }}>
            <div className="p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-full">
                  <Zap className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{ 
                    color: 'var(--color-text)',
                    textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}>{healthData.calories.burned}</div>
                  <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Calories Burned</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="insights">Insights</TabsTrigger>
          <TabsTrigger value="sync">Sync Status</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {healthData && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Heart Rate Zones */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-3">
                    <Heart className="w-5 h-5" />
                    <span>Heart Rate Zones</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Fat Burn</span>
                      <span className="text-sm font-semibold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{healthData.heartRate.zones.fatBurn} BPM</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Cardio</span>
                      <span className="text-sm font-semibold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{healthData.heartRate.zones.cardio} BPM</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Peak</span>
                      <span className="text-sm font-semibold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{healthData.heartRate.zones.peak} BPM</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Sleep Stages */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-3">
                    <Moon className="w-5 h-5" />
                    <span>Sleep Stages</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Deep Sleep</span>
                      <span className="text-sm font-semibold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{healthData.sleep.stages.deep}h</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Light Sleep</span>
                      <span className="text-sm font-semibold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{healthData.sleep.stages.light}h</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>REM Sleep</span>
                      <span className="text-sm font-semibold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{healthData.sleep.stages.rem}h</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Awake</span>
                      <span className="text-sm font-semibold">{healthData.sleep.stages.awake}h</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Additional Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-3">
                    <Gauge className="w-5 h-5" />
                    <span>Additional Metrics</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {healthData.weight && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Weight</span>
                        <span className="text-sm font-semibold">{healthData.weight} lbs</span>
                      </div>
                    )}
                    {healthData.bodyFat && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Body Fat</span>
                        <span className="text-sm font-semibold">{healthData.bodyFat}%</span>
                      </div>
                    )}
                    {healthData.bloodPressure && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Blood Pressure</span>
                        <span className="text-sm font-semibold">
                          {healthData.bloodPressure.systolic}/{healthData.bloodPressure.diastolic}
                        </span>
                      </div>
                    )}
                    {healthData.bloodGlucose && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Blood Glucose</span>
                        <span className="text-sm font-semibold">{healthData.bloodGlucose} mg/dL</span>
                      </div>
                    )}
                    {healthData.oxygenSaturation && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Oxygen Saturation</span>
                        <span className="text-sm font-semibold">{healthData.oxygenSaturation}%</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Calorie Balance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-3">
                    <Zap className="w-5 h-5" />
                    <span>Calorie Balance</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Burned</span>
                      <span className="text-sm font-semibold text-red-600">{healthData.calories.burned}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Consumed</span>
                      <span className="text-sm font-semibold text-green-600">{healthData.calories.consumed}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Net</span>
                      <span className={`text-sm font-semibold ${healthData.calories.net < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {healthData.calories.net}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Insights Tab */}
        <TabsContent value="insights" className="space-y-4">
          <div className="space-y-4">
            {insights.map((insight) => (
              <Card key={insight.id} className="hover:shadow-md transition-shadow">
                <CardContent className="sculptd-container-padding">
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-full ${getSeverityColor(insight.severity)}`}>
                      {getInsightIcon(insight.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-1">
                        <h4 className="font-semibold">{insight.title}</h4>
                        <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                          background: insight.severity === 'high' ? 'var(--color-error)' : 
                                     insight.severity === 'medium' ? 'var(--color-warning)' : 'var(--color-success)', 
                          color: 'var(--color-white)' 
                        }}>
                          {insight.severity}
                        </span>
                      </div>
                      <p className="text-sm mb-2" style={{ color: 'var(--color-text-muted)' }}>{insight.description}</p>
                      {insight.recommendations && insight.recommendations.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-text)' }}>Recommendations:</p>
                          <ul className="text-xs space-y-1" style={{ color: 'var(--color-text-muted)' }}>
                            {insight.recommendations.map((recommendation, index) => (
                              <li key={index} className="flex items-start space-x-1">
                                <span className="text-blue-500">•</span>
                                <span>{recommendation}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Sync Status Tab */}
        <TabsContent value="sync" className="space-y-4">
          <div className="space-y-4">
            {syncStatuses.map((status) => (
              <Card key={status.platform} className="hover:shadow-md transition-shadow">
                <CardContent className="sculptd-container-padding">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-full" style={{ background: 'var(--color-background-secondary)' }}>
                        {getPlatformIcon(status.platform)}
                      </div>
                      <div>
                        <h4 className="font-semibold capitalize">
                          {status.platform.replace('_', ' ')}
                        </h4>
                        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                          Last sync: {status.lastSync.toLocaleString()}
                        </p>
                        {status.nextSync && (
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                            Next sync: {status.nextSync.toLocaleString()}
                          </p>
                        )}
                        {status.errorMessage && (
                          <p className="text-xs text-red-600">{status.errorMessage}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1" style={{
                        background: status.status === 'connected' ? 'var(--color-success)' : 
                                   status.status === 'syncing' ? 'var(--color-warning)' : 'var(--color-error)', 
                        color: 'var(--color-white)' 
                      }}>
                        {getStatusIcon(status.status)}
                        <span className="capitalize">{status.status}</span>
                      </span>
                      {status.status === 'connected' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => syncHealthData(status.platform)}
                          disabled={syncing}
                        >
                          {syncing ? 'Syncing...' : 'Sync Now'}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => connectHealthPlatform(status.platform)}
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                  </div>
                  {status.dataTypes.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-text)' }}>Data Types:</p>
                      <div className="flex flex-wrap gap-1">
                        {status.dataTypes.map((type) => (
                          <span key={type} className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                            background: 'var(--color-secondary)', 
                            color: 'var(--color-white)' 
                          }}>
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-3">
                <Settings className="w-5 h-5" />
                <span>Health Data Settings</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Auto-sync</h4>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Automatically sync health data every 24 hours</p>
                  </div>
                  <Button variant="outline" size="sm">
                    Enable
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Data Privacy</h4>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Control how your health data is used</p>
                  </div>
                  <Button variant="outline" size="sm">
                    Configure
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Export Data</h4>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Download your health data</p>
                  </div>
                  <Button variant="outline" size="sm">
                    Export
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HealthDataPanel;
